import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './ModelComponentPositionList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelComponentPositionsState } from '../../reducers/initialState';
import ModelComponentPositionListTable from './ModelComponentPositionListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Cookies from 'js-cookie';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ModelCategoryConfigurationContentTemplate from '../ModelCategoryTab/ModelCategoryConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import { getModelCategoryPermission } from '../../utils';



const breadcrumbItems = [
  { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
  { label: <FormattedMessage id="modelComponentPositions.componentPositions" defaultMessage="Component positions" /> } // Last item has no href (current page)
];

const ModelComponentPositionList = ({
  modelComponentPositionManager = {
    ...initialModelComponentPositionsState.modelComponentPositionManager
  },
  userProfileWidgets,
  intl,
  getModelComponentPosition,
  saveModelComponentPosition,
  setCurrentPage,
  setFilterData,
  resetModelComponent,
  userModelCategories
}) => {
  const history = useNavigate();
  let { ModelCategoryId } = useParams();

  let filter = '';
  let offset = 0;
  let limit = 1000;


  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  useEffect(() => {
    if (modelComponentPositionManager.isOpSuccessful) {
      const timer = setTimeout(() => {
        getModelComponentPosition(offset, limit, filter, ModelCategoryId);
      }, 3000); // 3000 milliseconds = 3 seconds
      return () => clearTimeout(timer); // Cleanup function to prevent memory leaks
    } else {
      getModelComponentPosition(offset, limit, filter, ModelCategoryId);
    }
  }, [getModelComponentPosition, offset, limit, filter, modelComponentPositionManager.isOpSuccessful, ModelCategoryId]);

  useEffect(() => {
    return () => {
      resetModelComponent({ ...initialModelComponentPositionsState });
    };
  }, []);

  const [active, setActive] = useState(false);
  const [modelComponentPositionId, setModelComponentPositionId] = useState(0);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  const searchHandler = value => {
    setCurrentPage(1);
    filteredResults(value);
  };

  const filteredResults = value => {
    let filteredList = modelComponentPositionManager.modelComponentPositions;

    if (value !== '') {
      filteredList = filteredList.filter(function (item) {
        return item.Name.toLowerCase().includes(value.toLowerCase());
      });
    }

    setFilterData({ filteredResults: { filteredResults: filteredList } });
  };

  const modelComponentPositionListObj = Object.values(modelComponentPositionManager.filteredResults);


  const onRemoveModelComponentPositionClick = modelComponentPositionId => {
    setActive(true);
    setModelComponentPositionId(modelComponentPositionId);
  };

  const DeleteModelComponentPosition = () => {
    let saveData = modelComponentPositionManager.selectedModelComponentPosition;
    saveData.PerformDelete = true;
    saveData.ModelComponentPositionId = modelComponentPositionId;
    saveData.ModelCategoryId = ModelCategoryId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveModelComponentPosition(saveData, 'MODELCOMPONENTPOSITION_DELETE');
  };

  const handleClose = () => {
    setActive(false);
    setModelComponentPositionId(0);
  };

  const handleConfirm = () => {
    DeleteModelComponentPosition();
    handleClose();
  };

  const onAddNewClick = () => {
    history(`/operational-setup/model-category-configuration/${ModelCategoryId}/model-component-position/0`);
  };

  const onChangeModelComponentPositionClick = (modelComponentPositionId, parentComponentId = null) => {
    if (parentComponentId) {
      history(`/operational-setup/model-category-configuration/${ModelCategoryId}/model-component-position/${modelComponentPositionId}/${parentComponentId}`);
    } else {
      history(`/operational-setup/model-category-configuration/${ModelCategoryId}/model-component-position/${modelComponentPositionId}`);
    }
  };

  let messageId = (modelComponentPositionManager && modelComponentPositionManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELCOMPONENTPOSITION_EDIT');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELCOMPONENTPOSITION_DELETE');

  const dialogTitle = intl.formatMessage({
    id: 'modelComponentPositions.confirmDelete',
    defaultMessage: 'Are you sure you want to delete model component position?'
  });

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)


  return (
    <div className={s.modelComponentPosition}>
      {modelComponentPositionManager.isLoading && <LoadingSpinner />}
      {modelComponentPositionManager.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={modelComponentPositionManager.showBanner}
          status={modelComponentPositionManager.isOpSuccessful}
          successText={messageText}
        />
      )}
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />

      <ModelCategoryConfigurationContentTemplate selectedPage="manageComponentPosition" userProfileWidgets={userProfileWidgets}>
        <div className={s.innerComponent} id="model-component-position-section">
          <div className={s.modelComponentPositionHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="modelComponentPositions.componentPositions" defaultMessage="Component positions" />
              <span className={s.circle}>
                {modelComponentPositionManager.modelComponentPositions.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <Row className={s.titleRows}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                disabled={modelComponentPositionManager.showAddEditScreen}
                placeHolderTextId="modelComponentPositions.search"
                data-unittest="searchModelComponentPositions"
              />
            </Col>
            <Col sm={6} lg={8}>

              {isModelCategoryOwner && <Button
                className={s.addbutton}
                style={WidgetVisibility(userProfileWidgets, 'MODELCOMPONENTPOSITION_ADDNEW')}
                onClick={onAddNewClick}
                disabled={modelComponentPositionManager.showAddEditScreen}
                data-unittest="addModelComponentPositionButton"
              >
                + <FormattedMessage id="modelComponentPositions.addNew" defaultMessage="ADD NEW" />
              </Button>}
            </Col>
          </Row>

          <div>
            <Pagination
              data={modelComponentPositionListObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={modelComponentPositionManager.currentPage}
            >
              <ModelComponentPositionListTable
                RemoveModelComponentPosition={onRemoveModelComponentPositionClick}
                ChangeModelComponentPosition={onChangeModelComponentPositionClick}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                IsModelCategoryOwner={isModelCategoryOwner}
              /></Pagination>
          </div>

        </div>

      </ModelCategoryConfigurationContentTemplate>
    </div>
  );
};



ModelComponentPositionList.propTypes = {
  modelComponentPositionManager: PropTypes.object.isRequired,
  userProfileWidgets: PropTypes.object.isRequired,
  getModelComponentPosition: PropTypes.func.isRequired,
  saveModelComponentPosition: PropTypes.func.isRequired,
  setModelComponentPositionChange: PropTypes.func.isRequired
};

export default injectIntl(ModelComponentPositionList);
